// src/pages/MarriageReadinessTest.js
// Marriage Readiness Self-Test - Public assessment (no registration required)
// Premium glossy design with email results delivery

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  ChevronRight, 
  ChevronLeft, 
  Mail, 
  CheckCircle, 
  AlertTriangle,
  Sparkles,
  Shield,
  Brain,
  BookOpen,
  Eye,
  Users,
  Dna,
  Crown,
  Send,
  RotateCcw,
  Calendar,
  Phone,
  ArrowRight,
  ArrowUpRight,
  Check,
  X
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// ═══════════════════════════════════════════════════════════════
// ASSESSMENT DATA - 8 Sections with 47 Questions Total
// ═══════════════════════════════════════════════════════════════

const ASSESSMENT_SECTIONS = [
  {
    id: 1,
    title: "Motive Check",
    subtitle: "Why do I want to marry?",
    icon: Heart,
    color: "from-rose-500 to-pink-600",
    bgGlow: "bg-rose-500/20",
    warningThreshold: 2,
    warningMessage: "You may be acting from pressure, not purpose.",
    questions: [
      { id: 1, text: "If sex were not a factor, I would still want marriage." },
      { id: 2, text: "I want a lifetime partnership, not just a wedding day." },
      { id: 3, text: "I am not trying to escape loneliness, poverty, or family pressure." },
      { id: 4, text: "I would rather delay marriage than marry the wrong person." },
      { id: 5, text: "I am not marrying because \"my mates are already married.\"" },
      { id: 6, text: "I am not afraid of being left behind socially." }
    ]
  },
  {
    id: 2,
    title: "Emotional Health Check",
    subtitle: "Am I whole or looking to be completed?",
    icon: Brain,
    color: "from-violet-500 to-purple-600",
    bgGlow: "bg-violet-500/20",
    warningThreshold: 3,
    warningMessage: "You may need emotional healing before marriage.",
    questions: [
      { id: 7, text: "I am emotionally stable when single." },
      { id: 8, text: "I do not panic at the thought of being alone." },
      { id: 9, text: "I don't need constant validation to feel secure." },
      { id: 10, text: "I can regulate my emotions during conflict." },
      { id: 11, text: "I do not use silence, sex, anger, or withdrawal to punish people." },
      { id: 12, text: "I can apologize without feeling inferior." },
      { id: 13, text: "I can receive correction without collapsing." }
    ]
  },
  {
    id: 3,
    title: "Self-Awareness Check",
    subtitle: "Do I know my wounds?",
    icon: Eye,
    color: "from-amber-500 to-orange-600",
    bgGlow: "bg-amber-500/20",
    warningThreshold: 2,
    warningMessage: "You may enter marriage reactive and defensive.",
    questions: [
      { id: 14, text: "I am aware of my childhood emotional patterns." },
      { id: 15, text: "I know my triggers." },
      { id: 16, text: "I don't blame others for reactions caused by my own wounds." },
      { id: 17, text: "I am actively working on my baggage." },
      { id: 18, text: "I don't expect my partner to heal what I refuse to confront." }
    ]
  },
  {
    id: 4,
    title: "Doctrinal Alignment Check",
    subtitle: "Is this person spiritually compatible?",
    icon: BookOpen,
    color: "from-sky-500 to-blue-600",
    bgGlow: "bg-sky-500/20",
    warningThreshold: 3,
    warningMessage: "You are building on unstable spiritual foundation.",
    questions: [
      { id: 19, text: "Jesus is fully God and fully man." },
      { id: 20, text: "Jesus physically rose from the dead." },
      { id: 21, text: "Miracles are real and God still heals." },
      { id: 22, text: "The Holy Spirit is a gift, not something earned." },
      { id: 23, text: "Salvation is by grace through faith." },
      { id: 24, text: "Scripture is final authority over feelings and culture." }
    ]
  },
  {
    id: 5,
    title: "Character Over Gift Check",
    subtitle: "Am I attracted to maturity or performance?",
    icon: Crown,
    color: "from-emerald-500 to-teal-600",
    bgGlow: "bg-emerald-500/20",
    warningThreshold: 2,
    warningMessage: "Spiritual gifts can be loud. Character is quiet but durable.",
    questions: [
      { id: 25, text: "I am not impressed merely by spiritual gifts." },
      { id: 26, text: "I value character development more than charisma." },
      { id: 27, text: "I observe how they treat waiters, family, and strangers." },
      { id: 28, text: "I look at consistency, not vibes." },
      { id: 29, text: "I am not trying to \"fix\" or \"reform\" them." }
    ]
  },
  {
    id: 6,
    title: "Toxicity Screening",
    subtitle: "Is this relationship healthy?",
    icon: Shield,
    color: "from-red-500 to-rose-600",
    bgGlow: "bg-red-500/20",
    warningThreshold: 3,
    warningMessage: "Pause immediately. Reassess.",
    subsections: [
      { name: "Criticism", questions: [
        { id: 30, text: "I feel respected." },
        { id: 31, text: "When corrected, I feel guided, not attacked." },
        { id: 32, text: "I am not constantly made to feel inadequate." }
      ]},
      { name: "Gaslighting", questions: [
        { id: 33, text: "My perception is not constantly dismissed." },
        { id: 34, text: "I do not frequently doubt my memory after arguments." },
        { id: 35, text: "I am not told \"you're crazy\" or \"you're too sensitive\" often." }
      ]},
      { name: "Control & Isolation", questions: [
        { id: 36, text: "I am not pressured to cut off friends or family." },
        { id: 37, text: "My partner respects my autonomy." },
        { id: 38, text: "We do not operate in \"us against the world\" paranoia." }
      ]},
      { name: "Manipulation", questions: [
        { id: 39, text: "Love, intimacy, or silence is not used as punishment." },
        { id: 40, text: "Guilt is not used to control me." },
        { id: 41, text: "I feel safe expressing disagreement." }
      ]}
    ],
    questions: [] // Will be flattened from subsections
  },
  {
    id: 7,
    title: "Genetic & Practical Wisdom",
    subtitle: "Have we discussed the essentials?",
    icon: Dna,
    color: "from-cyan-500 to-sky-600",
    bgGlow: "bg-cyan-500/20",
    warningThreshold: 2,
    warningMessage: "Avoid unnecessary battles. Avoid preventable problems.",
    questions: [
      { id: 42, text: "We have discussed genotype." },
      { id: 43, text: "We have discussed children honestly." },
      { id: 44, text: "We have discussed money openly." },
      { id: 45, text: "We have discussed in-laws and culture." },
      { id: 46, text: "We are not bonding over a shared enemy." },
      { id: 47, text: "We are actually friends, not comrades in battle." }
    ]
  },
  {
    id: 8,
    title: "Identity Check",
    subtitle: "Do I know who I am in Christ?",
    icon: Sparkles,
    color: "from-fuchsia-500 to-pink-600",
    bgGlow: "bg-fuchsia-500/20",
    warningThreshold: 2,
    warningMessage: "If your identity collapses without them, you are not ready yet.",
    questions: [
      { id: 48, text: "My identity is not rooted in relationship status." },
      { id: 49, text: "I would still feel valuable if this relationship ended." },
      { id: 50, text: "I am not idolizing this person." },
      { id: 51, text: "I do not believe this is my \"only chance.\"" },
      { id: 52, text: "My peace is not dependent on this relationship." }
    ]
  }
];

// Flatten toxicity subsections into questions
ASSESSMENT_SECTIONS[5].questions = ASSESSMENT_SECTIONS[5].subsections.flatMap(s => s.questions);

// Total questions count
const TOTAL_QUESTIONS = ASSESSMENT_SECTIONS.reduce((sum, s) => sum + s.questions.length, 0);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const MarriageReadinessTest = () => {
  const navigate = useNavigate();

  // State
  const [currentView, setCurrentView] = useState('intro'); // intro, assessment, email, results
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [results, setResults] = useState(null);

  // Calculate progress
  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / TOTAL_QUESTIONS) * 100;

  // Handle answer
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Check if section is complete
  const isSectionComplete = (sectionIndex) => {
    const section = ASSESSMENT_SECTIONS[sectionIndex];
    return section.questions.every(q => answers[q.id] !== undefined);
  };

  // Calculate results
  const calculateResults = () => {
    let totalYes = 0;
    const sectionResults = [];

    ASSESSMENT_SECTIONS.forEach(section => {
      let sectionYes = 0;
      let sectionNo = 0;

      section.questions.forEach(q => {
        if (answers[q.id] === true) {
          sectionYes++;
          totalYes++;
        } else {
          sectionNo++;
        }
      });

      const needsAttention = sectionNo > section.warningThreshold;

      sectionResults.push({
        id: section.id,
        title: section.title,
        subtitle: section.subtitle,
        yesCount: sectionYes,
        noCount: sectionNo,
        total: section.questions.length,
        percentage: Math.round((sectionYes / section.questions.length) * 100),
        needsAttention,
        warningMessage: needsAttention ? section.warningMessage : null,
        questions: section.questions.map(q => ({
          text: q.text,
          answer: answers[q.id]
        }))
      });
    });

    const overallPercentage = Math.round((totalYes / TOTAL_QUESTIONS) * 100);
    
    let readinessLevel = '';
    let readinessColor = '';
    let readinessMessage = '';

    if (overallPercentage >= 85) {
      readinessLevel = 'Ready';
      readinessColor = 'text-emerald-400';
      readinessMessage = 'Emotionally and spiritually positioned for marriage.';
    } else if (overallPercentage >= 65) {
      readinessLevel = 'Growing';
      readinessColor = 'text-amber-400';
      readinessMessage = 'Growth needed, but foundation is present.';
    } else {
      readinessLevel = 'Pause & Build';
      readinessColor = 'text-rose-400';
      readinessMessage = 'Pause. Heal. Build. Grow. Marriage multiplies who you already are.';
    }

    return {
      totalYes,
      totalQuestions: TOTAL_QUESTIONS,
      overallPercentage,
      readinessLevel,
      readinessColor,
      readinessMessage,
      sectionResults,
      areasNeedingAttention: sectionResults.filter(s => s.needsAttention).length
    };
  };

  // Submit and send email
  const handleSubmit = async () => {
    if (!email || !name) return;
    
    setLoading(true);
    const calculatedResults = calculateResults();
    setResults(calculatedResults);

    try {
      // Call Firebase Cloud Function to send email
      const sendMarriageReadinessEmail = httpsCallable(functions, 'sendMarriageReadinessEmail');
      await sendMarriageReadinessEmail({
        email,
        name,
        phone,
        results: calculatedResults
      });
      setEmailSent(true);
    } catch (error) {
      console.error('Error sending email:', error);
      // Still show results even if email fails
    }
    
    setLoading(false);
    setCurrentView('results');
  };

  // Reset assessment
  const resetAssessment = () => {
    setAnswers({});
    setCurrentSection(0);
    setCurrentView('intro');
    setEmail('');
    setName('');
    setPhone('');
    setResults(null);
    setEmailSent(false);
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER: INTRO SCREEN
  // ═══════════════════════════════════════════════════════════════
  
  if (currentView === 'intro') {
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {/* Gradient orbs */}
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-gradient-to-br from-rose-500/30 to-pink-600/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-gradient-to-br from-violet-500/25 to-purple-600/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          {/* Header */}
          <header className="p-6 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2 text-white/60 hover:text-white transition">
              <Heart className="w-5 h-5" />
              <span className="text-sm font-medium">HeartSync</span>
            </a>
          </header>

          {/* Hero */}
          <main className="flex-1 flex items-center justify-center p-6">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-white/80">Free Self-Assessment</span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-rose-400 via-pink-400 to-violet-400 bg-clip-text text-transparent">
                  Marriage Readiness
                </span>
                <br />
                <span className="text-white">Self-Test</span>
              </h1>

              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-white/60 mb-4 max-w-2xl mx-auto">
                Be brutally honest with yourself.
              </p>
              <p className="text-lg text-white/40 mb-12 max-w-xl mx-auto">
                47 questions across 8 critical areas to assess your true readiness for marriage.
              </p>

              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-6 mb-12">
                {[
                  { value: '8', label: 'Sections' },
                  { value: '47', label: 'Questions' },
                  { value: '5', label: 'Minutes' },
                  { value: '∞', label: 'Insights' }
                ].map((stat, i) => (
                  <div key={i} className="px-6 py-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm">
                    <div className="text-3xl font-bold bg-gradient-to-r from-rose-400 to-violet-400 bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-sm text-white/50">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => setCurrentView('assessment')}
                className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-gradient-to-r from-rose-500 to-pink-600 text-white font-semibold text-lg shadow-2xl shadow-rose-500/25 hover:shadow-rose-500/40 transition-all duration-300 hover:scale-105"
              >
                <span>Begin Assessment</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                
                {/* Glossy shine effect */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-50" style={{ height: '50%' }} />
                </div>
              </button>

              {/* Trust indicators */}
              <div className="mt-12 flex items-center justify-center gap-6 text-white/40 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>100% Confidential</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>Results sent to your email</span>
                </div>
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="p-6 text-center text-white/30 text-sm">
            <p>RCCG Living Spring Morgantown • Where Good Things Never Cease</p>
          </footer>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER: ASSESSMENT SCREEN
  // ═══════════════════════════════════════════════════════════════

  if (currentView === 'assessment') {
    const section = ASSESSMENT_SECTIONS[currentSection];
    const SectionIcon = section.icon;
    const isLastSection = currentSection === ASSESSMENT_SECTIONS.length - 1;
    const canProceed = isSectionComplete(currentSection);

    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white">
        {/* Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute top-[-10%] left-[-5%] w-[400px] h-[400px] ${section.bgGlow} rounded-full blur-[100px] transition-all duration-1000`} />
          <div className="absolute bottom-[-10%] right-[-5%] w-[300px] h-[300px] bg-violet-500/10 rounded-full blur-[80px]" />
        </div>

        {/* Header with Progress */}
        <header className="sticky top-0 z-50 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setCurrentView('intro')}
                className="text-white/50 hover:text-white transition text-sm flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Exit
              </button>
              <div className="text-sm text-white/50">
                {answeredCount} of {TOTAL_QUESTIONS} answered
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${section.color} transition-all duration-500 ease-out`}
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Section indicators */}
            <div className="flex justify-between mt-3">
              {ASSESSMENT_SECTIONS.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setCurrentSection(i)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    i === currentSection 
                      ? `bg-gradient-to-r ${s.color} text-white shadow-lg` 
                      : isSectionComplete(i)
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-white/5 text-white/40 hover:bg-white/10'
                  }`}
                >
                  {isSectionComplete(i) && i !== currentSection ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    i + 1
                  )}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="relative z-10 max-w-4xl mx-auto px-6 py-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${section.color} shadow-2xl mb-4`}>
              <SectionIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Section {section.id}: {section.title}
            </h2>
            <p className="text-lg text-white/60">{section.subtitle}</p>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            {section.subsections ? (
              // Render subsections (for Toxicity Screening)
              section.subsections.map((subsection, subIndex) => (
                <div key={subIndex} className="mb-8">
                  <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4 pl-4">
                    {String.fromCharCode(65 + subIndex)}. {subsection.name}
                  </h3>
                  <div className="space-y-3">
                    {subsection.questions.map((question) => (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        answer={answers[question.id]}
                        onAnswer={(value) => handleAnswer(question.id, value)}
                        color={section.color}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              // Regular questions
              section.questions.map((question) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  answer={answers[question.id]}
                  onAnswer={(value) => handleAnswer(question.id, value)}
                  color={section.color}
                />
              ))
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-white/10">
            <button
              onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
              disabled={currentSection === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 text-white/60 hover:bg-white/10 hover:text-white transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            {isLastSection ? (
              <button
                onClick={() => setCurrentView('email')}
                disabled={!canProceed}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${
                  canProceed
                    ? `bg-gradient-to-r ${section.color} text-white shadow-lg hover:shadow-xl hover:scale-105`
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
              >
                Get Results
                <Sparkles className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => setCurrentSection(prev => prev + 1)}
                disabled={!canProceed}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${
                  canProceed
                    ? `bg-gradient-to-r ${section.color} text-white shadow-lg hover:shadow-xl hover:scale-105`
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
              >
                Next Section
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER: EMAIL COLLECTION SCREEN
  // ═══════════════════════════════════════════════════════════════

  if (currentView === 'email') {
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white flex items-center justify-center p-6">
        {/* Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] left-[30%] w-[500px] h-[500px] bg-gradient-to-br from-emerald-500/20 to-teal-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-[-10%] right-[20%] w-[400px] h-[400px] bg-gradient-to-br from-violet-500/15 to-purple-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="relative z-10 w-full max-w-lg">
          {/* Card */}
          <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-10 shadow-2xl overflow-hidden">
            {/* Glossy highlight */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '40%' }} />
            
            <div className="relative">
              {/* Icon */}
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-2xl shadow-emerald-500/30">
                <Mail className="w-8 h-8 text-white" />
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">
                Almost There!
              </h2>
              <p className="text-white/60 text-center mb-8">
                Enter your details to receive your personalized results
              </p>

              {/* Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/60 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/60 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(xxx) xxx-xxxx"
                    className="w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition"
                  />
                  <p className="text-xs text-white/40 mt-2">
                    For follow-up counseling session scheduling
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!email || !name || loading}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Get My Results
                    </>
                  )}
                </button>
              </form>

              <button
                onClick={() => setCurrentView('assessment')}
                className="w-full mt-4 text-white/40 hover:text-white/60 transition text-sm"
              >
                ← Back to assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER: RESULTS SCREEN
  // ═══════════════════════════════════════════════════════════════

  if (currentView === 'results' && results) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] text-white">
        {/* Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-15%] left-[20%] w-[500px] h-[500px] bg-gradient-to-br from-rose-500/20 to-pink-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-[-10%] right-[10%] w-[400px] h-[400px] bg-gradient-to-br from-violet-500/15 to-purple-500/10 rounded-full blur-[80px]" />
          <div className="absolute top-[50%] left-[-5%] w-[300px] h-[300px] bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-full blur-[60px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
          {/* Email Sent Badge */}
          {emailSent && (
            <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm mb-8 max-w-fit mx-auto">
              <CheckCircle className="w-4 h-4" />
              Results sent to {email}
            </div>
          )}

          {/* Main Score Card */}
          <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-12 mb-8 overflow-hidden">
            {/* Glossy shine */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '30%' }} />
            
            <div className="relative text-center">
              <h1 className="text-2xl text-white/60 mb-4">Your Readiness Score</h1>
              
              {/* Big Score */}
              <div className="relative w-48 h-48 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    className="fill-none stroke-white/10"
                    strokeWidth="12"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    className={`fill-none ${
                      results.overallPercentage >= 85 ? 'stroke-emerald-500' :
                      results.overallPercentage >= 65 ? 'stroke-amber-500' :
                      'stroke-rose-500'
                    }`}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${(results.overallPercentage / 100) * 553} 553`}
                    style={{ transition: 'stroke-dasharray 1s ease-out' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-5xl font-bold ${results.readinessColor}`}>
                    {results.overallPercentage}%
                  </span>
                  <span className="text-white/40 text-sm">YES responses</span>
                </div>
              </div>

              {/* Level Badge */}
              <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${
                results.overallPercentage >= 85 ? 'bg-emerald-500/20 border-emerald-500/30' :
                results.overallPercentage >= 65 ? 'bg-amber-500/20 border-amber-500/30' :
                'bg-rose-500/20 border-rose-500/30'
              } border mb-4`}>
                <Sparkles className={`w-5 h-5 ${results.readinessColor}`} />
                <span className={`font-semibold text-lg ${results.readinessColor}`}>
                  {results.readinessLevel}
                </span>
              </div>

              <p className="text-white/60 max-w-md mx-auto">
                {results.readinessMessage}
              </p>

              {results.areasNeedingAttention > 0 && (
                <div className="mt-6 flex items-center justify-center gap-2 text-amber-400">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{results.areasNeedingAttention} area{results.areasNeedingAttention > 1 ? 's' : ''} need{results.areasNeedingAttention === 1 ? 's' : ''} attention</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Plan CTA — TOP (prominent, can't miss) */}
          <div className="relative bg-gradient-to-br from-emerald-500/15 to-teal-500/10 backdrop-blur-xl rounded-3xl border border-emerald-500/25 p-8 mb-8 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '40%' }} />
            <div className="relative text-center">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 text-sm font-semibold ${
                results.overallPercentage >= 85
                  ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                  : results.overallPercentage >= 65
                  ? 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                  : 'bg-rose-500/20 border-rose-500/30 text-rose-400'
              }`}>
                {results.overallPercentage >= 85 ? '🟢 Track A: Ready' :
                 results.overallPercentage >= 65 ? '🟡 Track B: Growth' :
                 '🔴 Track C: Pause'}
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Your Personalized Action Plan is Ready
              </h3>
              <p className="text-white/60 mb-6 max-w-lg mx-auto text-sm">
                Teaching, scripture, prayer points, reflection questions and next steps — tailored to your track.
              </p>
              <button
                onClick={() => navigate('/action-plan', {
                  state: {
                    score: results.overallPercentage,
                    name: name,
                    sectionScores: results.sectionResults,
                  }
                })}
                className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <span>View My Action Plan</span>
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" style={{ height: '50%' }} />
              </button>
            </div>
          </div>

          {/* Section Breakdown */}
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Eye className="w-5 h-5 text-white/40" />
            Section Breakdown
          </h2>

          <div className="space-y-4 mb-12">
            {results.sectionResults.map((section) => {
              const SectionData = ASSESSMENT_SECTIONS.find(s => s.id === section.id);
              const SectionIcon = SectionData?.icon || Heart;
              
              return (
                <div 
                  key={section.id}
                  className={`relative bg-white/5 backdrop-blur-sm rounded-2xl border ${
                    section.needsAttention ? 'border-amber-500/30' : 'border-white/10'
                  } p-6 overflow-hidden`}
                >
                  {/* Warning indicator */}
                  {section.needsAttention && (
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[40px] border-t-amber-500/80 border-l-[40px] border-l-transparent">
                      <AlertTriangle className="absolute -top-9 right-1 w-4 h-4 text-white" />
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${SectionData?.color || 'from-gray-500 to-gray-600'} flex items-center justify-center flex-shrink-0`}>
                      <SectionIcon className="w-6 h-6 text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-white">{section.title}</h3>
                        <span className={`text-lg font-bold ${
                          section.percentage >= 80 ? 'text-emerald-400' :
                          section.percentage >= 60 ? 'text-amber-400' :
                          'text-rose-400'
                        }`}>
                          {section.percentage}%
                        </span>
                      </div>
                      <p className="text-sm text-white/40 mb-3">{section.subtitle}</p>

                      {/* Progress bar */}
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            section.percentage >= 80 ? 'bg-emerald-500' :
                            section.percentage >= 60 ? 'bg-amber-500' :
                            'bg-rose-500'
                          }`}
                          style={{ width: `${section.percentage}%` }}
                        />
                      </div>

                      <div className="text-sm text-white/50">
                        {section.yesCount} YES • {section.noCount} NO out of {section.total} questions
                      </div>

                      {section.needsAttention && section.warningMessage && (
                        <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-200">{section.warningMessage}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Final Question Card */}
          <div className="relative bg-gradient-to-br from-violet-500/20 to-purple-500/10 backdrop-blur-xl rounded-3xl border border-violet-500/20 p-8 mb-8 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '30%' }} />
            
            <div className="relative text-center">
              <h3 className="text-xl font-semibold text-white mb-4">⚠️ Final Question</h3>
              <p className="text-lg text-white/80 mb-4 italic">
                "If nothing changed about this person, and nothing changed about you,
                would you still say yes in 10 years?"
              </p>
              <p className="text-white/50">
                If the answer makes you uncomfortable, don't ignore that discomfort.
              </p>
            </div>
          </div>

          {/* Action Plan CTA */}
          <div className="relative bg-gradient-to-br from-emerald-500/15 to-teal-500/10 backdrop-blur-xl rounded-3xl border border-emerald-500/25 p-8 mb-6 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" style={{ height: '40%' }} />

            <div className="relative text-center">
              {/* Track badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-5 text-sm font-semibold ${
                results.overallPercentage >= 85
                  ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                  : results.overallPercentage >= 65
                  ? 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                  : 'bg-rose-500/20 border-rose-500/30 text-rose-400'
              }`}>
                {results.overallPercentage >= 85 ? '🟢 Track A: Ready' :
                 results.overallPercentage >= 65 ? '🟡 Track B: Growth' :
                 '🔴 Track C: Pause'}
              </div>

              <h3 className="text-2xl font-bold text-white mb-3">
                Your Personalized Action Plan is Ready
              </h3>
              <p className="text-white/60 mb-8 max-w-lg mx-auto">
                Based on your score, we've prepared a tailored pathway with teaching, 
                scripture, reflection questions, prayer points, and next steps — 
                specifically for your track.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {/* PRIMARY — Action Plan */}
                <button
                  onClick={() => navigate('/action-plan', {
                    state: {
                      score: results.overallPercentage,
                      name: name,
                      sectionScores: results.sectionResults,
                    }
                  })}
                  className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-105 overflow-hidden"
                >
                  <span>View My Action Plan</span>
                  <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" style={{ height: '50%' }} />
                </button>

                {/* SECONDARY — Book Session */}
                <a
                  href={`mailto:pastor@rccglivingspring.org?subject=Marriage%20Counseling%20Request&body=Hi%20Pastor,%0A%0AI%20just%20completed%20the%20Marriage%20Readiness%20Self-Test%20and%20would%20like%20to%20schedule%20a%20counseling%20session.%0A%0AName:%20${encodeURIComponent(name)}%0AScore:%20${results.overallPercentage}%25%0APhone:%20${encodeURIComponent(phone || 'Not provided')}%0A%0AThank%20you!`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition"
                >
                  <Calendar className="w-5 h-5" />
                  Book Counseling Session
                </a>
              </div>

              <button
                onClick={resetAssessment}
                className="mt-4 text-white/30 hover:text-white/50 transition text-sm flex items-center gap-1.5 mx-auto"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Retake Assessment
              </button>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-white/30">
            RCCG Living Spring Morgantown • Where Good Things Never Cease
          </p>
        </div>
      </div>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════
// QUESTION CARD COMPONENT
// ═══════════════════════════════════════════════════════════════

const QuestionCard = ({ question, answer, onAnswer, color }) => {
  return (
    <div className={`relative bg-white/5 backdrop-blur-sm rounded-xl border transition-all duration-300 ${
      answer !== undefined 
        ? answer 
          ? 'border-emerald-500/30 bg-emerald-500/5' 
          : 'border-rose-500/30 bg-rose-500/5'
        : 'border-white/10 hover:border-white/20'
    } p-5`}>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <p className="text-white/90 leading-relaxed">{question.text}</p>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => onAnswer(true)}
            className={`w-16 h-10 rounded-lg font-semibold text-sm transition-all ${
              answer === true
                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                : 'bg-white/5 text-white/50 hover:bg-emerald-500/20 hover:text-emerald-400 border border-white/10'
            }`}
          >
            YES
          </button>
          <button
            onClick={() => onAnswer(false)}
            className={`w-16 h-10 rounded-lg font-semibold text-sm transition-all ${
              answer === false
                ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/30'
                : 'bg-white/5 text-white/50 hover:bg-rose-500/20 hover:text-rose-400 border border-white/10'
            }`}
          >
            NO
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarriageReadinessTest;
